import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { useI18n } from "@/context/I18nContext";
import Logo from "@/components/Logo";
import {
  Upload, FileText, X, Loader2, CheckCircle2, IndianRupee, Wifi, WifiOff,
  Sun, Moon, Languages, Printer, CreditCard, Copy, ArrowLeft,
  Clock, ShieldCheck, User, Phone, FileType, ImageIcon, UserSquare,
  Plus, Minus, RotateCw, RotateCcw, Sparkles,
} from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import ImageEditor from "@/components/ImageEditor";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import DocumentCropEditor from "@/components/DocumentCropEditor";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const STATUS_COPY = {
  pending: { label: "In queue", color: "bg-amber-500/10 text-amber-600 border-amber-500/20", icon: Clock },
  printing: { label: "Printing now", color: "bg-brand-blue/10 text-brand-blue border-brand-blue/20", icon: Printer },
  completed: { label: "Ready — collect", color: "bg-brand-green/10 text-brand-green border-brand-green/20", icon: CheckCircle2 },
  failed: { label: "Failed — refund issued", color: "bg-destructive/10 text-destructive border-destructive/20", icon: X },
  cancelled: { label: "Cancelled", color: "bg-muted text-muted-foreground border-border", icon: X },
};

const DOC_PAPER = ["A4", "A5", "A3", "Letter", "Legal"];
const PHOTO_PAPER = ["A4", "A5", "A3", "4x6", "5x7"];
const PASSPORT_SIZES = [
  { key: "IN_PASSPORT", label: "India Passport (35×45)" },
  { key: "PAN_CARD", label: "PAN Card (25×35)" },
  { key: "AADHAAR", label: "Aadhaar (35×45)" },
  { key: "US_VISA", label: "US Visa (51×51)" },
  { key: "SCHENGEN", label: "Schengen (35×45)" },
  { key: "UK_PASSPORT", label: "UK Passport (35×45)" },
];

const PRINT_TYPES = [
  { key: "document", label: "Document", desc: "PDF / print any file", icon: FileType, accepts: "application/pdf,image/jpeg,image/png,image/webp" },
  { key: "photo", label: "Photo", desc: "JPG / PNG glossy print", icon: ImageIcon, accepts: "image/jpeg,image/png,image/webp" },
  { key: "passport", label: "Passport", desc: "Multi-copy passport sheet", icon: UserSquare, accepts: "image/jpeg,image/png" },
];

async function countPdfPages(file) {
  try {
    const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/legacy/build/pdf.worker.mjs",
      import.meta.url,
    ).toString();
    const buf = await file.arrayBuffer();
    const doc = await pdfjs.getDocument({ data: buf }).promise;
    return doc.numPages;
  } catch (e) {
    return 1;
  }
}

export default function ShopWalkin() {
  const { slug } = useParams();
  const { theme, toggle } = useTheme();
  const { locale, setLocale, languages } = useI18n();
  const [shop, setShop] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  // Multi-file batch state — used only when print_type === "document". Each
  // entry: { key, file, copies, name, size, pages }. Photo/passport flows
  // still use the single `file` state above.
  const [batchFiles, setBatchFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [preview, setPreview] = useState(null);
  const [pdfPages, setPdfPages] = useState(null); // client-side page count
  // Image editor modal — opens when the customer selects a JPG/PNG for a
  // Photo print so they can crop / rotate / zoom before paying.
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSrc, setEditorSrc] = useState(null);
  const [editorName, setEditorName] = useState("photo.jpg");

  // Before/after enhancement preview — fetched from /shop/{slug}/enhance-preview
  // whenever a JPG/PNG is picked with enhance ON. Shown inline so the customer
  // can see the scanner-quality result on the right before paying.
  const [enhancePreviewUrl, setEnhancePreviewUrl] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  // Data-URL preview of the single image currently in the document batch
  // queue (used as the "before" image in the scanner-quality slider).
  const [batchDocPreview, setBatchDocPreview] = useState(null);

  // True document crop — when a customer uploads a JPG/PNG in Document mode
  // we auto-open the crop editor. `cropCorners` is stored per batch-file key
  // so the correct quad is sent per file when there are multiple documents.
  const [cropOpen, setCropOpen] = useState(false);
  const [cropTarget, setCropTarget] = useState(null); // { key, file, url }
  const [cropCornersByKey, setCropCornersByKey] = useState({});    // key → [[x,y],x4]
  const [croppedPreviewByKey, setCroppedPreviewByKey] = useState({}); // key → object URL of cropped result
  const [form, setForm] = useState({
    print_type: "document",
    passport_size: "IN_PASSPORT",
    copies: 1, color: false, duplex: false,
    paper_size: "A4", orientation: "portrait",
    customer_name: "", customer_phone: "",
    // Auto-enhance for uploads. Default ON — customers overwhelmingly want
    // the scanner-quality output. Kept as a tri-state so the UI can show
    // "off / document scan / photo boost" without a second toggle.
    enhance_mode: "auto",   // auto | off | document | photo
  });
  const [upload, setUpload] = useState(null);
  const [rotating, setRotating] = useState(false);
  // Cache-buster for the passport preview <img>. Bumped every time we PATCH
  // the rotation so the browser refetches the freshly-rendered JPEG.
  const [previewNonce, setPreviewNonce] = useState(0);
  const [orderInfo, setOrderInfo] = useState(null);
  const [paying, setPaying] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await publicApi.get(`/shop/${slug}`);
        setShop(data);
        // If the shop was reached via a legacy slug (old printed QR / bookmark),
        // silently rewrite the URL to the canonical one so future navigation,
        // sharing and refreshes use the current slug — without a hard redirect.
        if (data?.slug_redirected && data?.shop_slug && data.shop_slug !== slug) {
          try {
            window.history.replaceState(null, "", `/shop/${data.shop_slug}`);
          } catch { /* no-op */ }
        }
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || "Shop not found");
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!orderInfo?.track_token) return;
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await publicApi.get(`/shop/track/${orderInfo.order_id}?token=${orderInfo.track_token}`);
        if (alive) setTracking(data);
      } catch { /* keep last */ }
    };
    poll();
    const iv = setInterval(poll, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, [orderInfo]);

  // Live cost estimate --------------------------------------------------------
  const estimate = useMemo(() => {
    if (!shop) return null;
    const p = shop.pricing;
    if (form.print_type === "passport") {
      // Passport: flat glossy-paper price (server uses price_passport_sheet).
      const sub = p.price_passport_sheet || p.price_color;
      const gst = (sub * p.gst_pct) / 100;
      return { pages: 1, subtotal: sub, gst, total: sub + gst };
    }
    // Document mode with a batch of files → sum every file's pages × copies.
    if (form.print_type === "document" && batchFiles.length > 0) {
      const isColor = form.color;
      const perPage = isColor ? p.price_color : p.price_bw;
      const duplexExtra = form.duplex ? p.price_duplex_extra : 0;
      let pages = 0;
      let sub = 0;
      let anyEstimated = false;
      for (const f of batchFiles) {
        const filePages = f.pages || 1;
        if (!f.pages) anyEstimated = true;
        pages += filePages * f.copies;
        sub += (perPage + duplexExtra) * filePages * f.copies;
      }
      const gst = (sub * p.gst_pct) / 100;
      return { pages, subtotal: sub, gst, total: sub + gst, estimated: anyEstimated };
    }
    // photo → 1 page; document w/o batch → uploaded pdf pages OR 1 if not counted yet
    const pages = form.print_type === "photo" ? 1 : (pdfPages || 1);
    // Photos now honour the customer's Black & White / Color choice — same
    // as documents. Passport is separate (flat glossy price).
    const isColor = form.color;
    const perPage = isColor ? p.price_color : p.price_bw;
    const duplexExtra = form.duplex ? p.price_duplex_extra : 0;
    const sub = (perPage + duplexExtra) * pages * form.copies;
    const gst = (sub * p.gst_pct) / 100;
    return { pages, subtotal: sub, gst, total: sub + gst, estimated: form.print_type === "document" && !pdfPages };
  }, [shop, form, pdfPages, batchFiles]);

  const handleFile = (f) => {
    // Documents flow into the multi-file batch queue automatically. Photo /
    // passport stay single-file so the existing composition previews work.
    if (form.print_type === "document") {
      return addBatchFiles([f]);
    }
    const lower = f.name.toLowerCase();
    const okImg = /(jpe?g|png|webp)$/.test(lower);
    if (form.print_type === "photo" && !okImg) return toast.error("JPG / PNG / WebP only");
    if (form.print_type === "passport" && !/(jpe?g|png)$/.test(lower)) return toast.error("JPG or PNG only");
    if (f.size > 15 * 1024 * 1024) return toast.error("Max 15 MB");

    // For Photo prints, launch the crop/rotate/zoom editor before we save the
    // file. Passport keeps its own dedicated rotation step, and non-photo
    // (documents) goes through the batch flow above.
    if (form.print_type === "photo" && okImg) {
      const r = new FileReader();
      r.onload = (e) => {
        setEditorSrc(e.target.result);
        setEditorName(f.name);
        setEditorOpen(true);
      };
      r.readAsDataURL(f);
      return;
    }

    setFile(f);
    setPdfPages(null);
    if (okImg) {
      const r = new FileReader();
      r.onload = (e) => {
        setPreview(e.target.result);
        // For non-photo images (document mode with a single file, or passport),
        // pull the enhancement preview now so the customer sees before/after
        // without needing to open the editor.
        fetchEnhancePreview(f);
      };
      r.readAsDataURL(f);
    } else {
      setPreview(null);
      setEnhancePreviewUrl(null);
      countPdfPages(f).then((n) => setPdfPages(n));
    }
  };

  const onEditorConfirm = (editedFile) => {
    setEditorOpen(false);
    setFile(editedFile);
    setPdfPages(null);
    const r = new FileReader();
    r.onload = (e) => {
      setPreview(e.target.result);
      // After the customer finished cropping, request the scanner-quality
      // preview so they can see the enhancement before paying.
      fetchEnhancePreview(editedFile);
    };
    r.readAsDataURL(editedFile);
    toast.success("Image ready to print");
  };

  /**
   * Ask the backend to enhance the given image and cache the resulting JPEG
   * URL so <BeforeAfterSlider> can display it. No-op when enhance is off or
   * for non-image / passport uploads.
   */
  const fetchEnhancePreview = async (f) => {
    if (!f || !slug || form.enhance_mode === "off" || form.print_type === "passport") {
      setEnhancePreviewUrl(null);
      return;
    }
    const lower = (f.name || "").toLowerCase();
    if (!/(jpe?g|png|webp)$/.test(lower) && f.type && !f.type.startsWith("image/")) {
      setEnhancePreviewUrl(null);
      return;
    }
    setEnhancing(true);
    // Free the previous blob if any so we don't leak
    if (enhancePreviewUrl) URL.revokeObjectURL(enhancePreviewUrl);
    setEnhancePreviewUrl(null);
    try {
      const mode = form.print_type === "photo" ? "photo" : "document";
      const fd = new FormData();
      fd.append("file", f);
      fd.append("mode", mode);
      // Send stored crop corners (document mode single-image batch) so the
      // Before/After slider reflects the customer's confirmed crop exactly.
      if (mode === "document" && form.print_type === "document") {
        const only = batchFiles.length === 1 ? batchFiles[0] : null;
        if (only && cropCornersByKey[only.key]) {
          fd.append("corners", JSON.stringify(cropCornersByKey[only.key]));
        }
      }
      const res = await publicApi.post(`/shop/${slug}/enhance-preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        responseType: "blob",
      });
      setEnhancePreviewUrl(URL.createObjectURL(res.data));
    } catch (e) {
      // Non-blocking — if the enhancement service hiccups, we simply hide the
      // slider. The upload flow still works and the server will still enhance
      // when the customer pays.
    } finally {
      setEnhancing(false);
    }
  };

  // ------- Document crop editor -------------------------------------------
  const doAutoDetectCorners = async () => {
    if (!cropTarget || !slug) return { corners: null };
    const fd = new FormData();
    fd.append("file", cropTarget.file);
    const { data } = await publicApi.post(`/shop/${slug}/detect-corners`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  };

  const doRenderCropPreview = async (corners) => {
    if (!cropTarget || !slug) throw new Error("No target");
    const fd = new FormData();
    fd.append("file", cropTarget.file);
    fd.append("mode", "document");
    fd.append("corners", JSON.stringify(corners));
    const res = await publicApi.post(`/shop/${slug}/enhance-preview`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      responseType: "blob",
    });
    return res.data;
  };

  const closeCropEditor = () => {
    if (cropTarget?.url) URL.revokeObjectURL(cropTarget.url);
    setCropTarget(null);
    setCropOpen(false);
  };

  const onCropConfirm = (corners, previewUrl) => {
    if (!cropTarget) return closeCropEditor();
    const key = cropTarget.key;
    setCropCornersByKey((m) => ({ ...m, [key]: corners }));
    // Cache the cropped preview so the Before/After slider on the main page
    // shows THIS crop instead of firing another enhance-preview roundtrip.
    // Take a copy of the object URL — the editor will revoke its own on unmount.
    if (previewUrl) {
      // Refresh the slider's cache: fetch the cropped bytes via the same
      // endpoint so we control the object URL and its life-cycle.
      doRenderCropPreview(corners)
        .then((blob) => {
          const u = URL.createObjectURL(blob);
          setCroppedPreviewByKey((prev) => {
            const next = { ...prev };
            if (next[key]) URL.revokeObjectURL(next[key]);
            next[key] = u;
            return next;
          });
          // Also update the main Before/After slider url so the customer
          // sees the confirmed crop right away.
          setEnhancePreviewUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return u;
          });
          // NB: we intentionally reuse `u` — revoking it in the slider vs.
          // in this map is fine as long as only one revoke fires. We store
          // it in `croppedPreviewByKey` and update the slider ref to the
          // same URL, but the slider setter above already revoked its old
          // one. On file remove OR batch clear we revoke from the map.
        })
        .catch(() => { /* no-op — customer can still upload */ });
    }
    toast.success("Crop saved — only this area will print");
    closeCropEditor();
  };

  // Add one or many files to the document batch. Auto-counts PDF pages so the
  // per-file page × copies pricing lights up immediately. JPG/PNG count as 1
  // page each. Bad extensions get rejected with a toast per file.
  const addBatchFiles = (fs) => {
    const accepted = [];
    for (const f of fs) {
      const lower = f.name.toLowerCase();
      const okPdf = lower.endsWith(".pdf");
      const okImg = /(jpe?g|png|webp)$/.test(lower);
      if (!okPdf && !okImg) { toast.error(`${f.name}: PDF or JPG/PNG only`); continue; }
      if (okPdf && f.size > 15 * 1024 * 1024) { toast.error(`${f.name}: max 15 MB`); continue; }
      if (okImg && f.size > 10 * 1024 * 1024) { toast.error(`${f.name}: max 10 MB`); continue; }
      accepted.push({
        key: `${f.name}-${f.size}-${Date.now()}-${Math.random()}`,
        file: f, name: f.name, size: f.size, copies: 1,
        pages: okImg ? 1 : null, // PDFs get counted async below
      });
    }
    if (accepted.length + batchFiles.length > 20) {
      return toast.error("Max 20 files per order");
    }
    setBatchFiles((prev) => [...prev, ...accepted]);
    // Count PDF pages asynchronously so the estimate refreshes when ready.
    accepted.forEach((entry) => {
      if (entry.pages == null) {
        countPdfPages(entry.file).then((n) =>
          setBatchFiles((prev) => prev.map((b) => b.key === entry.key ? { ...b, pages: n } : b)),
        );
      }
    });
    // Document mode — auto-open the true crop editor for the FIRST added
    // image so the customer defines exactly what will be printed. Only
    // triggered for images (PDFs skip cropping entirely).
    if (form.print_type === "document" && form.enhance_mode !== "off") {
      const firstImage = accepted.find((a) => /(jpe?g|png|webp)$/i.test(a.name));
      if (firstImage) {
        const url = URL.createObjectURL(firstImage.file);
        setCropTarget({ key: firstImage.key, file: firstImage.file, url });
        setCropOpen(true);
      }
    }
  };

  const removeBatchFile = (key) => {
    setBatchFiles((prev) => prev.filter((b) => b.key !== key));
    // Also clear stored crop artefacts for the removed key so we don't leak.
    setCropCornersByKey((m) => { const n = { ...m }; delete n[key]; return n; });
    setCroppedPreviewByKey((m) => {
      const n = { ...m };
      if (n[key]) URL.revokeObjectURL(n[key]);
      delete n[key];
      return n;
    });
  };
  const setBatchCopies = (key, copies) =>
    setBatchFiles((prev) => prev.map((b) => b.key === key ? { ...b, copies: Math.max(1, Math.min(50, copies)) } : b));

  // Document mode uses the batch queue. When the customer uploads a SINGLE
  // JPG/PNG in that queue we still want to show the scanner-quality preview
  // (Aadhaar / PAN etc. are almost always a single card). Multi-file batches
  // and PDF batches skip the slider — it wouldn't be useful.
  useEffect(() => {
    if (form.print_type !== "document") return;
    if (batchFiles.length !== 1) {
      if (batchDocPreview) URL.revokeObjectURL(batchDocPreview);
      setBatchDocPreview(null);
      return;
    }
    const b = batchFiles[0];
    const lower = b.name.toLowerCase();
    const isImage = /(jpe?g|png|webp)$/.test(lower);
    if (!isImage) {
      if (batchDocPreview) URL.revokeObjectURL(batchDocPreview);
      setBatchDocPreview(null);
      return;
    }
    const r = new FileReader();
    r.onload = (e) => setBatchDocPreview(e.target.result);
    r.readAsDataURL(b.file);
    fetchEnhancePreview(b.file);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchFiles, form.print_type, form.enhance_mode]);

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDrag(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (!dropped.length) return;
    if (form.print_type === "document") {
      addBatchFiles(dropped);
    } else {
      handleFile(dropped[0]);
    }
  }, [form.print_type, batchFiles.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitUpload = async () => {
    if (form.print_type === "document") {
      if (!batchFiles.length) return toast.error("Add at least one file");
      setUploading(true);
      try {
        const fd = new FormData();
        for (const b of batchFiles) fd.append("files", b.file);
        fd.append("copies", JSON.stringify(batchFiles.map((b) => b.copies)));
        fd.append("color", String(form.color));
        fd.append("duplex", String(form.duplex));
        fd.append("paper_size", form.paper_size);
        fd.append("orientation", form.orientation);
        if (form.customer_name) fd.append("customer_name", form.customer_name);
        if (form.customer_phone) fd.append("customer_phone", form.customer_phone);
        // enhance_mode=auto → document lane for the multi-file walk-in flow
        // (customers scan Aadhaar/PAN cards etc. here); "off" skips enhancement.
        fd.append("enhance_mode", form.enhance_mode === "auto" ? "document" : form.enhance_mode);
        // Per-file confirmed crop corners — same order as `files`. `null` for
        // PDFs or images the customer didn't crop. When present, the server
        // WILL restrict the print output to exactly this quad — no more
        // "whole photo prints" bug.
        fd.append("crop_corners_list", JSON.stringify(
          batchFiles.map((b) => cropCornersByKey[b.key] || null),
        ));
        const { data } = await publicApi.post(`/shop/${slug}/upload-batch`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setUpload(data);
        setStep(2);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
      } finally {
        setUploading(false);
      }
      return;
    }
    if (!file) return toast.error("Choose a file first");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("print_type", form.print_type);
      fd.append("passport_size", form.passport_size);
      fd.append("copies", String(form.copies));
      fd.append("color", String(form.print_type === "passport" ? true : form.color));
      fd.append("duplex", String(form.print_type === "passport" ? false : form.duplex));
      fd.append("paper_size", form.print_type === "passport" ? "A4" : form.paper_size);
      fd.append("orientation", form.orientation);
      if (form.customer_name) fd.append("customer_name", form.customer_name);
      if (form.customer_phone) fd.append("customer_phone", form.customer_phone);
      // Server-side auto-enhance. `auto` picks: document for print_type=document,
      // photo for print_type=photo, off for passport (has its own preview flow).
      let enhanceMode = "off";
      if (form.enhance_mode === "off" || form.print_type === "passport") {
        enhanceMode = "off";
      } else if (form.enhance_mode === "auto") {
        enhanceMode = form.print_type === "photo" ? "photo" : "document";
      } else {
        enhanceMode = form.enhance_mode; // explicit "document" or "photo"
      }
      fd.append("enhance_mode", enhanceMode);
      const { data } = await publicApi.post(`/shop/${slug}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUpload(data);
      setStep(2);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Apply a rotation delta (±90 or 180) to the current passport upload. Stores
  // the resulting absolute rotation on the walkin_upload doc and regenerates
  // both the print PDF and the preview JPEG server-side so the customer sees
  // exactly what the retailer will print.
  const rotatePassport = async (delta) => {
    if (!upload || upload.print_type !== "passport" || rotating) return;
    const current = upload.passport_layout?.rotation_deg || 0;
    const next = (((current + delta) % 360) + 360) % 360;
    setRotating(true);
    try {
      const fd = new FormData();
      fd.append("rotation_deg", String(next));
      await publicApi.patch(
        `/shop/${slug}/passport-upload/${upload.upload_id}/rotation`, fd,
      );
      setUpload((u) => ({
        ...u,
        passport_layout: { ...(u.passport_layout || {}), rotation_deg: next },
      }));
      setPreviewNonce((n) => n + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rotate failed");
    } finally {
      setRotating(false);
    }
  };

  const doPay = async () => {
    if (!upload) return;
    setPaying(true);
    try {
      const fd = new FormData();
      fd.append("upload_id", upload.upload_id);
      const { data: order } = await publicApi.post(`/shop/${slug}/pay`, fd);
      // Fail fast if backend responded with a mock provider — this only happens
      // in a mis-configured deployment where RAZORPAY_KEY_ID is not set. Opening
      // Razorpay Checkout with "mock_key" causes an instant "Payment Failed"
      // popup with no actionable message.
      if (order.provider !== "razorpay" || !order.key_id?.startsWith("rzp_")) {
        toast.error(
          "Payments not configured on this deployment. Ask the site admin to set RAZORPAY_KEY_ID and RAZORPAY_KEY_SECRET in the environment."
        );
        setPaying(false);
        return;
      }
      // Live Razorpay only — mock provider is only returned in dev when keys are absent.
      const ok = await loadRazorpay();
      if (!ok) return toast.error("Razorpay failed to load");
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount_paise, currency: order.currency,
        order_id: order.order_id, name: shop.shop_name,
        description: `Print ${upload.filename}`,
        prefill: { name: form.customer_name || "", contact: form.customer_phone || "" },
        theme: { color: "#0F62FE" },
        handler: async (res) => {
          try {
            const { data } = await publicApi.post(`/shop/${slug}/confirm`, {
              upload_id: upload.upload_id,
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
            });
            setOrderInfo(data);
            setStep(3);
          } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.on("payment.failed", async (r) => {
        // Log full error details so we can diagnose from backend logs even
        // when the user only sees a generic "Payment Failed" toast.
        const err = r?.error || {};
        console.error("[Razorpay payment.failed]", err);
        try {
          await publicApi.post(`/shop/${slug}/payment-failed`, {
            upload_id: upload.upload_id,
            order_id: order?.order_id || null,
            error_code: err.code || null,
            error_description: err.description || null,
            error_source: err.source || null,
            error_step: err.step || null,
            error_reason: err.reason || null,
            error_metadata: err.metadata || null,
          }).catch(() => {});
        } catch (_) { /* best-effort */ }
        // Show the merchant-actionable message when Razorpay gives one.
        const msg = err.description
          ? `${err.description}${err.reason ? ` (${err.reason})` : ""}`
          : "Payment failed. Please try again.";
        toast.error(msg);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Payment failed");
    } finally {
      setPaying(false);
    }
  };

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setPdfPages(null);
    setUpload(null); setOrderInfo(null); setTracking(null);
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <Card className="p-8 max-w-md text-center border-border">
        <X className="mx-auto text-destructive" size={40} />
        <h1 className="mt-4 font-display text-2xl font-bold">Shop not found</h1>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
      </Card>
    </div>
  );
  if (!shop) return <div className="min-h-screen flex items-center justify-center bg-background">
    <Loader2 className="animate-spin text-primary" size={32} /></div>;

  const StatusIcon = tracking && STATUS_COPY[tracking.status]?.icon;
  const currentType = PRINT_TYPES.find((t) => t.key === form.print_type);
  const paperOptions = form.print_type === "photo" ? PHOTO_PAPER : DOC_PAPER;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border bg-background/85 backdrop-blur sticky top-0 z-20">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-1">
            <Select value={locale} onValueChange={setLocale}>
              <SelectTrigger className="h-8 w-auto gap-1 border-0 px-2" data-testid="shop-lang-select">
                <Languages size={14} /><SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {languages.map((l) => <SelectItem key={l.code} value={l.code}>{l.native}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="shop-theme-toggle">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-6 space-y-6 pb-32">
        {/* Shop info */}
        <Card className="p-5 border-border bg-card" data-testid="shop-info">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Walk-in print</p>
              <h1 className="mt-1 text-xl font-display font-bold tracking-tight truncate">{shop.shop_name}</h1>
              <p className="text-xs text-muted-foreground mt-1">Operated by {shop.retailer_name}</p>
            </div>
            <Badge variant="outline" className={shop.online ? "bg-brand-green/10 text-brand-green border-brand-green/20" : "bg-muted"}>
              {shop.online ? <><Wifi size={12} className="mr-1" /> Open</> : <><WifiOff size={12} className="mr-1" /> Offline</>}
            </Badge>
          </div>
        </Card>

        {/* Stepper */}
        <div className="flex items-center gap-2" data-testid="shop-stepper">
          {[1, 2, 3].map((n) => (
            <div key={n} className={`flex-1 h-1 rounded-full ${step >= n ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground text-center -mt-4">
          Step {step} of 3 · {["Print details", "Pay online", "Track"][step - 1]}
        </p>

        {step === 1 && (
          <div className="space-y-4" data-testid="step-upload">
            {/* Print type */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">1. Print type</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {PRINT_TYPES.map((tp) => {
                  const Icon = tp.icon;
                  const active = form.print_type === tp.key;
                  return (
                    <button
                      key={tp.key}
                      type="button"
                      onClick={() => { setForm({ ...form, print_type: tp.key }); setFile(null); setPreview(null); setPdfPages(null); }}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        active ? "border-primary bg-primary/10 text-primary" : "border-border hover:border-primary/50 bg-card"}`}
                      data-testid={`type-${tp.key}`}
                    >
                      <Icon size={18} className="mb-1" />
                      <p className="text-sm font-medium">{tp.label}</p>
                      <p className="text-[10px] opacity-70 mt-0.5">{tp.desc}</p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Upload */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                2. {form.print_type === "document" ? "Upload files" : "Upload file"}
              </Label>
              <Card className="mt-2 p-4 border-border bg-card">
                {/* Document mode: multi-file batch UI. Always renders the drop zone
                    (so more files can be added), then a list of accepted files
                    with per-file copies control and remove button. */}
                {form.print_type === "document" ? (
                  <div className="space-y-3">
                    <label
                      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={() => setDrag(false)}
                      onDrop={onDrop}
                      className={`block cursor-pointer rounded-xl border-2 border-dashed p-6 text-center transition-all
                        ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                      data-testid="shop-dropzone"
                    >
                      <div className="h-11 w-11 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                        <Upload size={20} />
                      </div>
                      <p className="mt-2 font-display text-sm font-medium">
                        {batchFiles.length === 0 ? "Tap to add PDFs & images" : "Add more files"}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        PDF, JPG, PNG · up to 20 files · one payment for the whole batch
                      </p>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" multiple className="hidden"
                        onChange={(e) => {
                          const fs = Array.from(e.target.files || []);
                          if (fs.length) addBatchFiles(fs);
                          e.target.value = "";
                        }}
                        data-testid="shop-file-input" />
                    </label>
                    {batchFiles.length > 0 && (
                      <div className="space-y-2" data-testid="shop-batch-list">
                        {batchFiles.map((b) => (
                          <div key={b.key}
                               className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-background"
                               data-testid={`shop-batch-item-${b.key}`}>
                            <div className="h-10 w-10 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                              {/(jpe?g|png|webp)$/i.test(b.name) ? <ImageIcon size={16} /> : <FileText size={16} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{b.name}</p>
                              <p className="text-[11px] text-muted-foreground">
                                {(b.size / 1024).toFixed(1)} KB
                                {b.pages != null ? ` · ${b.pages} page${b.pages === 1 ? "" : "s"}` : " · counting…"}
                              </p>
                            </div>
                            <div className="flex items-center gap-1.5">
                              {/(jpe?g|png|webp)$/i.test(b.name) && form.enhance_mode !== "off" && (
                                <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]"
                                  onClick={() => {
                                    const url = URL.createObjectURL(b.file);
                                    setCropTarget({ key: b.key, file: b.file, url });
                                    setCropOpen(true);
                                  }}
                                  data-testid={`shop-batch-recrop-${b.key}`}>
                                  {cropCornersByKey[b.key] ? "Re-crop" : "Crop"}
                                </Button>
                              )}
                              <Button variant="outline" size="icon" className="h-7 w-7"
                                onClick={() => setBatchCopies(b.key, b.copies - 1)}
                                data-testid={`shop-batch-copies-minus-${b.key}`}>
                                <Minus size={12} />
                              </Button>
                              <span className="w-7 text-center text-sm font-display font-semibold tabular-nums"
                                    data-testid={`shop-batch-copies-value-${b.key}`}>{b.copies}</span>
                              <Button variant="outline" size="icon" className="h-7 w-7"
                                onClick={() => setBatchCopies(b.key, b.copies + 1)}
                                data-testid={`shop-batch-copies-plus-${b.key}`}>
                                <Plus size={12} />
                              </Button>
                              <span className="text-[10px] text-muted-foreground pl-1 hidden sm:inline">copies</span>
                            </div>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive"
                              onClick={() => removeBatchFile(b.key)}
                              data-testid={`shop-batch-remove-${b.key}`}>
                              <X size={14} />
                            </Button>
                          </div>
                        ))}
                        <div className="flex items-center justify-between px-1 pt-1 text-[11px] text-muted-foreground">
                          <span data-testid="shop-batch-summary">
                            {batchFiles.length} file{batchFiles.length === 1 ? "" : "s"} ·{" "}
                            {batchFiles.reduce((s, b) => s + (b.pages || 0) * b.copies, 0)} pages to print
                          </span>
                        </div>

                        {/* Scanner-quality Before / After preview for a single-image
                            document batch (Aadhaar / PAN / DL / RC etc.). Multi-file
                            batches and PDFs skip this — it wouldn't apply. */}
                        {form.enhance_mode !== "off" && batchDocPreview && (
                          <div className="pt-2" data-testid="shop-before-after-wrap">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs uppercase tracking-widest text-brand-green font-semibold flex items-center gap-1">
                                <Sparkles size={11} /> Scanner-quality preview
                              </p>
                              {enhancing && (
                                <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                                  <Loader2 size={11} className="animate-spin" /> Enhancing…
                                </p>
                              )}
                            </div>
                            {enhancing && !enhancePreviewUrl ? (
                              <div className="w-full h-[280px] rounded-lg border border-border bg-secondary/40 animate-pulse" />
                            ) : enhancePreviewUrl ? (
                              <BeforeAfterSlider
                                originalSrc={batchDocPreview}
                                enhancedSrc={enhancePreviewUrl}
                                height={280}
                              />
                            ) : null}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : !file ? (
                  <label
                    onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                    onDragLeave={() => setDrag(false)}
                    onDrop={onDrop}
                    className={`block cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all
                      ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/50"}`}
                    data-testid="shop-dropzone"
                  >
                    <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center mx-auto">
                      <Upload size={22} />
                    </div>
                    <p className="mt-3 font-display text-base font-medium">Tap to upload</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {form.print_type === "photo" ? "JPG, PNG, WebP · max 10 MB" :
                       "JPG, PNG portrait · max 10 MB"}
                    </p>
                    <input type="file" accept={currentType.accepts} className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                      data-testid="shop-file-input" />
                  </label>
                ) : (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-background">
                    {preview ? (
                      <img src={preview} alt="" className="h-14 w-14 rounded-md object-cover flex-shrink-0" />
                    ) : (
                      <div className="h-14 w-14 rounded-md bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <FileText size={22} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" data-testid="shop-filename">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                        {pdfPages ? ` · ${pdfPages} pages` : ""}
                      </p>
                    </div>
                    {form.print_type === "photo" && preview && (
                      <Button variant="outline" size="sm"
                        onClick={() => { setEditorSrc(preview); setEditorName(file.name); setEditorOpen(true); }}
                        data-testid="shop-edit-image-btn">
                        Edit
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => { setFile(null); setPreview(null); setPdfPages(null); if (enhancePreviewUrl) URL.revokeObjectURL(enhancePreviewUrl); setEnhancePreviewUrl(null); }} data-testid="shop-clear-file">
                      <X size={16} />
                    </Button>
                  </div>
                )}

                {/* Before / After scanner-quality preview — only for images
                    with enhance ON, and never for passport (which has its own
                    layout preview). We show a subtle skeleton while the
                    /enhance-preview call is in flight so the section doesn't
                    jump when the enhanced image arrives. */}
                {file && preview && form.enhance_mode !== "off" && form.print_type !== "passport" && (
                  <div className="mt-3" data-testid="shop-before-after-wrap">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-widest text-brand-green font-semibold flex items-center gap-1">
                        <Sparkles size={11} /> Scanner-quality preview
                      </p>
                      {enhancing && (
                        <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Loader2 size={11} className="animate-spin" /> Enhancing…
                        </p>
                      )}
                    </div>
                    {enhancing && !enhancePreviewUrl ? (
                      <div className="w-full h-[280px] rounded-lg border border-border bg-secondary/40 animate-pulse" />
                    ) : enhancePreviewUrl ? (
                      <BeforeAfterSlider
                        originalSrc={preview}
                        enhancedSrc={enhancePreviewUrl}
                        height={280}
                      />
                    ) : null}
                  </div>
                )}
              </Card>
            </div>

            {/* Options */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">3. Options</Label>
              <Card className="mt-2 p-4 border-border bg-card space-y-4">
                {form.print_type === "passport" ? (
                  <>
                    <div>
                      <Label>Passport size</Label>
                      <Select value={form.passport_size} onValueChange={(v) => setForm({ ...form, passport_size: v })}>
                        <SelectTrigger className="mt-1.5" data-testid="passport-size-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {PASSPORT_SIZES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {/* Passport photo settings are fixed — 4×6" glossy paper, 8 photos, portrait, 300 DPI.
                        This mirrors the retailer's Glossy-Paper-Loaded confirmation flow so what the
                        customer sees on the preview is exactly what comes out of the printer. */}
                    <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-4 space-y-2"
                         data-testid="passport-fixed-config">
                      <div className="flex items-center gap-2">
                        <ImageIcon size={14} className="text-primary" />
                        <p className="text-sm font-medium">4×6 inch Glossy Photo Paper</p>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[11px] text-muted-foreground">
                        <div>Layout · <span className="text-foreground font-medium">4 cols × 2 rows</span></div>
                        <div>Photos · <span className="text-foreground font-medium">8 per sheet</span></div>
                        <div>Orientation · <span className="text-foreground font-medium">Portrait</span></div>
                        <div>Resolution · <span className="text-foreground font-medium">300 DPI</span></div>
                      </div>
                      <p className="text-[11px] text-muted-foreground leading-snug pt-1">
                        The retailer will load glossy photo paper and confirm before printing — no A4 waste.
                      </p>
                    </div>
                  </>
                ) : (
                  <div>
                    <Label>Paper size</Label>
                    <Select value={form.paper_size} onValueChange={(v) => setForm({ ...form, paper_size: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="shop-paper-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{paperOptions.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}

                {form.print_type !== "passport" && (
                  <div className="p-3 rounded-lg border border-brand-green/30 bg-brand-green/5" data-testid="shop-enhance-card">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="flex items-center gap-1.5">
                          <Sparkles size={13} className="text-brand-green" />
                          Auto-enhance <span className="text-[10px] uppercase tracking-widest text-brand-green">Scanner quality</span>
                        </Label>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {form.print_type === "document"
                            ? "Auto-crop, straighten & scanner-clean your Aadhaar / PAN / documents."
                            : "Auto-fix brightness, sharpness & colour on photos before printing."}
                        </p>
                      </div>
                      <Switch
                        checked={form.enhance_mode !== "off"}
                        onCheckedChange={(v) => {
                          setForm({ ...form, enhance_mode: v ? "auto" : "off" });
                          if (v) {
                            if (file) fetchEnhancePreview(file);
                          } else {
                            if (enhancePreviewUrl) URL.revokeObjectURL(enhancePreviewUrl);
                            setEnhancePreviewUrl(null);
                          }
                        }}
                        data-testid="shop-enhance-switch"
                      />
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      Only applies to JPG / PNG uploads. PDFs are printed as-is. Passport photos have their own layout preview.
                    </p>
                  </div>
                )}

                {form.print_type !== "passport" && (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div>
                      <Label>Color</Label>
                      <p className="text-xs text-muted-foreground">
                        {form.color ? `Color · ₹${shop.pricing.price_color}/pg` : `Black & White · ₹${shop.pricing.price_bw}/pg`}
                      </p>
                    </div>
                    <Switch checked={form.color} onCheckedChange={(v) => setForm({ ...form, color: v })} data-testid="shop-color-switch" />
                  </div>
                )}

                {form.print_type === "document" && (
                  <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                    <div>
                      <Label>Double-side (duplex)</Label>
                      <p className="text-xs text-muted-foreground">
                        {form.duplex ? `+₹${shop.pricing.price_duplex_extra}/page` : "Single-side"}
                      </p>
                    </div>
                    <Switch checked={form.duplex} onCheckedChange={(v) => setForm({ ...form, duplex: v })} data-testid="shop-duplex-switch" />
                  </div>
                )}

                {/* Document mode uses per-file copies inside the batch list,
                    so the global "Copies" row would be confusing — hide it. */}
                {form.print_type !== "document" && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-border">
                  <div>
                    <Label>{form.print_type === "passport" ? "Photos per sheet" : "Copies"}</Label>
                    <p className="text-xs text-muted-foreground">
                      {form.print_type === "passport"
                        ? "Fixed at 8 (4 × 2) on the 4×6\" glossy sheet"
                        : "Number of prints"}
                    </p>
                  </div>
                  {form.print_type === "passport" ? (
                    <span className="w-8 text-center font-display font-semibold tabular-nums" data-testid="shop-copies-fixed">8</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setForm({ ...form, copies: Math.max(1, form.copies - 1) })}
                        data-testid="shop-copies-minus">
                        <Minus size={14} />
                      </Button>
                      <span className="w-8 text-center font-display font-semibold tabular-nums" data-testid="shop-copies-value">{form.copies}</span>
                      <Button variant="outline" size="icon" className="h-8 w-8"
                        onClick={() => setForm({ ...form, copies: Math.min(100, form.copies + 1) })}
                        data-testid="shop-copies-plus">
                        <Plus size={14} />
                      </Button>
                    </div>
                  )}
                </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Name (optional)</Label>
                    <div className="relative mt-1.5">
                      <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                        placeholder="Rahul" className="pl-9" data-testid="shop-name-input" />
                    </div>
                  </div>
                  <div>
                    <Label>Phone (optional)</Label>
                    <div className="relative mt-1.5">
                      <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })}
                        placeholder="+91…" className="pl-9" data-testid="shop-phone-input" />
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {step === 2 && upload && (
          <div className="space-y-4" data-testid="step-pay">
            {upload.print_type === "passport" && upload.preview_url && (
              <Card className="p-6 border-border bg-card" data-testid="passport-sheet-preview-card">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">
                      Your glossy sheet preview
                    </p>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Exactly how the 8 photos will print on 4×6" glossy paper.
                    </p>
                  </div>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                    8 photos · 300 DPI
                  </Badge>
                </div>
                <div className="mx-auto flex items-center justify-center rounded-lg border border-border bg-secondary/40 p-4 relative">
                  <img
                    src={`${BACKEND_URL}${upload.preview_url}?v=${previewNonce}`}
                    alt="Passport sheet preview"
                    className="max-h-[440px] shadow-md rounded"
                    data-testid="passport-sheet-preview-img"
                  />
                  {rotating && (
                    <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-lg">
                      <Loader2 className="animate-spin text-primary" size={22} />
                    </div>
                  )}
                </div>
                {/* Rotation control — 0/90/180/270 clockwise. Uses the walk-in
                    PATCH endpoint that regenerates BOTH the printable PDF and
                    the preview JPEG on the server so what the customer sees
                    is what the retailer prints. */}
                <div className="mt-4 flex items-center gap-2 justify-center flex-wrap"
                     data-testid="passport-rotation-controls">
                  <p className="text-xs text-muted-foreground mr-1">If your face is sideways or upside-down, rotate:</p>
                  <Button variant="outline" size="sm" disabled={rotating}
                    onClick={() => rotatePassport(-90)}
                    data-testid="passport-rotate-left">
                    <RotateCcw size={14} className="mr-1" /> 90° left
                  </Button>
                  <Button variant="outline" size="sm" disabled={rotating}
                    onClick={() => rotatePassport(90)}
                    data-testid="passport-rotate-right">
                    <RotateCw size={14} className="mr-1" /> 90° right
                  </Button>
                  <Button variant="outline" size="sm" disabled={rotating}
                    onClick={() => rotatePassport(180)}
                    data-testid="passport-rotate-flip">
                    180°
                  </Button>
                </div>
              </Card>
            )}
            <Card className="p-6 border-border bg-card">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Review & pay</p>
              <div className="mt-4 divide-y divide-border">
                <div className="py-3 flex justify-between text-sm">
                  <span className="text-muted-foreground">Print type</span>
                  <span className="font-medium capitalize">{upload.print_type}</span>
                </div>
                {upload.items && upload.items.length > 0 ? (
                  <div className="py-3">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Files ({upload.file_count})</span>
                      <span className="font-medium tabular-nums">{upload.total_pages} pages</span>
                    </div>
                    <div className="space-y-1.5" data-testid="shop-review-batch-list">
                      {upload.items.map((it, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs bg-secondary/40 rounded px-2 py-1.5">
                          <FileText size={12} className="text-muted-foreground flex-shrink-0" />
                          <span className="flex-1 min-w-0 truncate font-medium">{it.filename}</span>
                          <span className="text-muted-foreground tabular-nums">{it.pages}p × {it.copies}</span>
                          <span className="tabular-nums font-medium">₹{Number(it.subtotal).toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="py-3 flex justify-between text-sm">
                      <span className="text-muted-foreground">File</span>
                      <span className="font-medium max-w-[240px] truncate">{upload.filename}</span>
                    </div>
                    <div className="py-3 flex justify-between text-sm">
                      <span className="text-muted-foreground">Pages × Copies</span>
                      <span className="font-medium tabular-nums">{upload.total_pages} × {upload.copies}</span>
                    </div>
                  </>
                )}
                <div className="py-3 flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="tabular-nums">₹{upload.subtotal.toFixed(2)}</span>
                </div>
                <div className="py-3 flex justify-between text-sm">
                  <span className="text-muted-foreground">GST</span>
                  <span className="tabular-nums">₹{upload.gst.toFixed(2)}</span>
                </div>
                <div className="py-3 flex justify-between items-baseline">
                  <span className="text-sm text-muted-foreground">Total</span>
                  <span className="text-3xl font-display font-bold tabular-nums text-brand-green" data-testid="shop-total">
                    ₹{upload.cost.toFixed(2)}
                  </span>
                </div>
              </div>
              <Button onClick={doPay} disabled={paying}
                className="w-full h-12 mt-4 bg-primary hover:bg-brand-blue-hover text-base"
                data-testid="shop-pay-btn">
                {paying ? <><Loader2 size={16} className="animate-spin mr-2" /> Processing…</> :
                 <><CreditCard size={16} className="mr-2" /> Pay ₹{upload.cost.toFixed(2)}</>}
              </Button>
              <p className="text-[11px] text-center text-muted-foreground mt-2 flex items-center justify-center gap-1">
                <ShieldCheck size={12} /> Secured by Razorpay
              </p>
              <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="w-full mt-2 text-muted-foreground" data-testid="shop-back-btn">
                <ArrowLeft size={14} className="mr-1" /> Change details
              </Button>
            </Card>
          </div>
        )}

        {step === 3 && orderInfo && (
          <Card className="p-6 border-border bg-card text-center" data-testid="step-track">
            <div className="h-16 w-16 mx-auto rounded-full bg-brand-green/10 text-brand-green flex items-center justify-center">
              <CheckCircle2 size={32} />
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold">Payment successful!</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Your print is queued at <span className="text-foreground font-medium">{shop.shop_name}</span>.
            </p>
            {tracking && (
              <div className="mt-6">
                <Badge variant="outline" className={`${STATUS_COPY[tracking.status]?.color} text-sm px-3 py-1`} data-testid="shop-status-badge">
                  {StatusIcon && <StatusIcon size={14} className="mr-1.5" />}
                  {STATUS_COPY[tracking.status]?.label || tracking.status}
                </Badge>
                <div className="mt-4 text-xs text-muted-foreground space-y-1">
                  <p>Printer: <span className="text-foreground font-medium">{tracking.printer_name || "—"}</span></p>
                  <p>Order ID: <span className="font-mono">{orderInfo.order_id.slice(0, 8)}</span></p>
                </div>
              </div>
            )}
            <div className="mt-6 flex gap-2">
              <Button variant="outline" onClick={reset} className="flex-1" data-testid="shop-new-print-btn">
                New print
              </Button>
              <Button
                onClick={() => navigator.clipboard.writeText(`${window.location.href}?order=${orderInfo.order_id}&token=${orderInfo.track_token}`).then(() => toast.success("Link copied"))}
                variant="outline" className="flex-1" data-testid="shop-share-btn"
              >
                <Copy size={14} className="mr-1" /> Share
              </Button>
            </div>
          </Card>
        )}
      </main>

      {/* Sticky bottom total + CTA (step 1 only) */}
      {step === 1 && estimate && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-border bg-background/95 backdrop-blur">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Total {estimate.estimated && "(est.)"}
              </p>
              <p className="text-2xl font-display font-bold tabular-nums text-brand-green" data-testid="shop-live-total">
                ₹{estimate.total.toFixed(2)}
              </p>
              <p className="text-[10px] text-muted-foreground">
                {form.print_type === "document" && batchFiles.length > 0
                  ? `${estimate.pages} page${estimate.pages !== 1 ? "s" : ""} · ${batchFiles.length} file${batchFiles.length !== 1 ? "s" : ""} · incl. GST`
                  : `${estimate.pages} page${estimate.pages !== 1 ? "s" : ""} × ${form.copies} · incl. GST`}
              </p>
            </div>
            <Button
              onClick={submitUpload}
              disabled={uploading || (form.print_type === "document" ? batchFiles.length === 0 : !file)}
              className="h-12 px-6 bg-primary hover:bg-brand-blue-hover text-base"
              data-testid="shop-continue-btn"
            >
              {uploading ? <><Loader2 size={16} className="animate-spin mr-2" /> Uploading…</> :
               <>Pay & print <IndianRupee size={16} className="ml-1" /></>}
            </Button>
          </div>
        </div>
      )}

      <ImageEditor
        open={editorOpen}
        src={editorSrc}
        filename={editorName}
        client={publicApi}
        renderEndpoint={`/shop/${slug}/editor/render`}
        detectCornersEndpoint={`/shop/${slug}/detect-corners`}
        onCancel={() => setEditorOpen(false)}
        onConfirm={onEditorConfirm}
      />

      <DocumentCropEditor
        open={cropOpen}
        file={cropTarget?.file}
        originalUrl={cropTarget?.url}
        autoDetectCorners={doAutoDetectCorners}
        renderPreview={doRenderCropPreview}
        onCancel={closeCropEditor}
        onConfirm={onCropConfirm}
      />

      <Toaster richColors position="top-center" />
    </div>
  );
}
